import React, { useEffect, useState } from 'react';
import { authToken } from '../../authToken';
import TimePicker from '../../components/TimePicker';
import { API_BASE_URL } from '../../config';

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_REGISTERED_EVENT = `${ENDPOINT_HOST}/registeredevent`;
const ENDPOINT_REGISTERED_EVENT_TYPE = `${ENDPOINT_HOST}/registeredeventtype`;

export default function DailyEventsAddPanel({ onClose = null }) {
  const [selectedTimestamp, setSelectedTimestamp] = useState(Date.now());
  const [eventTypes, setEventTypes] = useState([]);
  const [eventType, setEventType] = useState('');
  const [loadingEventTypes, setLoadingEventTypes] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEventTypes = async () => {
      setLoadingEventTypes(true);

      try {
        const response = await fetch(ENDPOINT_REGISTERED_EVENT_TYPE, {
          headers: {
            ...(authToken ? { 'Authorization': authToken } : {})
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch registered event types (${response.status})`);
        }

        const data = await response.json();
        const sortedTypes = Array.from(
          new Set(
            (Array.isArray(data) ? data : [])
              .map((item) => String(item?.eventtype || '').trim())
              .filter(Boolean)
          )
        ).sort((a, b) => a.localeCompare(b));

        setEventTypes(sortedTypes);
        setEventType((current) => current || sortedTypes[0] || '');
      } catch (e) {
        console.error('Failed to fetch registered event types:', e);
        setEventTypes([]);
        setError(e?.message || 'Failed to fetch registered event types.');
      } finally {
        setLoadingEventTypes(false);
      }
    };

    fetchEventTypes();
  }, []);

  const handleTimeChange = (timestamp) => {
    setSelectedTimestamp(timestamp);
  };

  const handleCancel = () => {
    setSelectedTimestamp(Date.now());
    setEventType(eventTypes[0] || '');
    setError(null);
    if (onClose) onClose();
  };

  const handleOk = async () => {
    if (!eventType) {
      setError('Event type is required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(ENDPOINT_REGISTERED_EVENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': authToken } : {})
        },
        body: JSON.stringify({
          timestamp: selectedTimestamp,
          eventtype: eventType
        })
      });

      if (response.ok) {
        if (onClose) onClose();
      } else {
        const errorData = await response.text();
        setError(`Failed to add daily event entry: ${errorData} (${response.status})`);
      }
    } catch (e) {
      console.error('Failed to add daily event entry:', e);
      setError('Failed to add daily event entry. Please check your connection.');
    } finally {
      setTimeout(() => setIsSubmitting(false), 1000);
    }
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '100px'
  };

  return (
    <div
      style={{
        padding: '10px 0',
        maxWidth: '400px',
        margin: '0 auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <div>
        <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>
          📅 Add Daily Event
        </h3>

        {error && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              textAlign: 'center',
              border: '1px solid #f5c6cb'
            }}
          >
            ⚠️ {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '16px'
          }}
        >
          <TimePicker initialTimestamp={selectedTimestamp} onTimeChange={handleTimeChange} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            disabled={loadingEventTypes || eventTypes.length === 0}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ced4da',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              boxSizing: 'border-box'
            }}
          >
            {loadingEventTypes && <option value="">Loading event types...</option>}
            {!loadingEventTypes && eventTypes.length === 0 && <option value="">No event types found</option>}
            {eventTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          marginTop: 'auto'
        }}
      >
        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          style={{
            ...buttonStyle,
            backgroundColor: '#6c757d',
            color: 'white',
            opacity: isSubmitting ? 0.6 : 1
          }}
          onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = '#5a6268')}
          onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = '#6c757d')}
        >
          Cancel
        </button>
        <button
          onClick={handleOk}
          disabled={isSubmitting}
          style={{
            ...buttonStyle,
            backgroundColor: '#f6e96b',
            color: '#333',
            opacity: isSubmitting ? 0.6 : 1
          }}
          onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = '#f4e456')}
          onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = '#f6e96b')}
        >
          {isSubmitting ? 'Adding...' : 'OK'}
        </button>
      </div>
    </div>
  );
}